import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import { FileMetaDataDTO } from '../services/GetCount';
import { SearchableTableHeader } from './SearchableTableHeader';

interface DataTableProps {
    chartKey: string;
    data: FileMetaDataDTO[];
    orderBy: string | undefined;
    order: 'asc' | 'desc' | undefined;
    showSearch: boolean | undefined;
    searchColumn: string | undefined;
    searchTerm: string | undefined;
    currentPage: number;
    rowsPerPage: number;
    selectedRows: Set<number>;
    onSort: (property: string) => void;
    onSearchClick: (column: string) => void;
    onSearchChange: (value: string) => void;
    onSearchBlur: () => void;
    onPageChange: (newPage: number) => void;
    onRowClick: (index: number) => void;
    onDownloadAll: () => void;
    onDownloadSelected: () => void;
    onClearFilter: () => void;
}

export const DataTable: React.FC<DataTableProps> = ({
    chartKey,
    data,
    orderBy,
    order,
    showSearch,
    searchColumn,
    searchTerm,
    currentPage,
    rowsPerPage,
    selectedRows,
    onSort,
    onSearchClick,
    onSearchChange,
    onSearchBlur,
    onPageChange,
    onRowClick,
    onDownloadAll,
    onDownloadSelected,
    onClearFilter,
}) => {
    const [jumpToPage, setJumpToPage] = useState('');
    const [jumpToPageError, setJumpToPageError] = useState<string | null>(null);

    const totalPages = Math.ceil(data.length / rowsPerPage);

    const handleJumpToPage = () => {
        const pageNumber1Based = parseInt(jumpToPage, 10);

        if (Number.isNaN(pageNumber1Based)) {
            setJumpToPageError('Please enter a valid page number');
            return;
        }

        if (pageNumber1Based < 1 || pageNumber1Based > totalPages) {
            setJumpToPageError(`Page ${pageNumber1Based} does not exist. Enter 1 - ${totalPages}`);
            return;
        }

        onPageChange(pageNumber1Based - 1); // Convert to 0-based index
        setJumpToPage('');
        setJumpToPageError(null);
    };

    const handleJumpInputKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleJumpToPage();
        }
    };
    const getStatusStyle = (status: string) => {
        switch (status.toUpperCase()) {
            case 'SUCCESS':
                return { backgroundColor: '#d4edda', color: '#155724', fontWeight: 'bold' };
            case 'PARTIAL':
                return { backgroundColor: '#fff3cd', color: '#856404', fontWeight: 'bold' };
            case 'ERROR':
                return { backgroundColor: '#f8d7da', color: '#721c24', fontWeight: 'bold' };
            default:
                return {};
        }
    };

    const paginatedData = data.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

    return (
        <TableContainer component={Paper} variant="outlined" elevation={3} sx={{ padding: 0 }}>
            <Table sx={{ fontSize: '12px', borderCollapse: 'collapse' }}>
                <TableHead>
                    <TableRow sx={{ height: '0px', backgroundColor: '#e0e0e0', '& th': { fontWeight: 'bold', borderBottom: '0px solid #999' } }}>
                        <SearchableTableHeader
                            columnKey="fileName"
                            label="File Name"
                            chartKey={chartKey}
                            orderBy={orderBy}
                            order={order}
                            showSearch={showSearch}
                            searchColumn={searchColumn}
                            searchTerm={searchTerm}
                            onSort={onSort}
                            onSearchClick={() => onSearchClick('fileName')}
                            onSearchChange={onSearchChange}
                            onSearchBlur={onSearchBlur}
                            placeholder="Search filenames..."
                        />
                        <SearchableTableHeader
                            columnKey="status"
                            label="Status"
                            chartKey={chartKey}
                            orderBy={orderBy}
                            order={order}
                            showSearch={showSearch}
                            searchColumn={searchColumn}
                            searchTerm={searchTerm}
                            onSort={onSort}
                            onSearchClick={() => onSearchClick('status')}
                            onSearchChange={onSearchChange}
                            onSearchBlur={onSearchBlur}
                            placeholder="Search status..."
                        />
                        <SearchableTableHeader
                            columnKey="createdUser"
                            label="Uploaded By"
                            chartKey={chartKey}
                            orderBy={orderBy}
                            order={order}
                            showSearch={showSearch}
                            searchColumn={searchColumn}
                            searchTerm={searchTerm}
                            onSort={onSort}
                            onSearchClick={() => onSearchClick('createdUser')}
                            onSearchChange={onSearchChange}
                            onSearchBlur={onSearchBlur}
                            placeholder="Search users..."
                        />
                        <TableCell className="data-table-cell time" sx={{ padding: '0px', fontSize: '12px' }}>
                            {showSearch && searchColumn === 'createTs' ? (
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    placeholder="Search dates..."
                                    value={searchTerm || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
                                    onBlur={onSearchBlur}
                                    autoFocus
                                    sx={{
                                        marginLeft: '8px',
                                        width: '120px',
                                        '& .MuiOutlinedInput-root': {
                                            height: '24px',
                                            fontSize: '11px',
                                            backgroundColor: 'white',
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '4px 8px',
                                        },
                                    }}
                                />
                            ) : (
                                <TableSortLabel
                                    active={orderBy === 'createTs'}
                                    direction={order || 'asc'}
                                    onClick={() => onSort('createTs')}
                                >
                                    Uploaded Time
                                </TableSortLabel>
                            )}
                            {!(showSearch && searchColumn === 'createTs') && (
                                <span
                                    style={{ color: '#666', cursor: 'pointer', marginLeft: '4px', fontSize: '12px' }}
                                    onClick={() => onSearchClick('createTs')}
                                    title="Search dates"
                                >
                                    🔍
                                </span>
                            )}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedData.length === 0 ? (
                        <TableRow sx={{ height: '25px' }}>
                            <TableCell colSpan={4} sx={{ padding: '4px', fontSize: '12px', textAlign: 'center' }}>
                                No data matching the given condition.
                            </TableCell>
                        </TableRow>
                    ) : (
                        paginatedData.map((row, index) => {
                            const globalIndex = currentPage * rowsPerPage + index;
                            const isSelected = selectedRows.has(globalIndex);

                            return (
                                <TableRow
                                    key={globalIndex}
                                    onClick={() => onRowClick(globalIndex)}
                                    sx={{
                                        height: '25px',
                                        cursor: 'pointer',
                                        ...(isSelected
                                            ? {
                                                backgroundColor: '#fff3cd',
                                                '&:hover': { backgroundColor: '#ffeaa7' }
                                            }
                                            : {
                                                '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                                '&:hover': { backgroundColor: '#e0e0e0' }
                                            }
                                        ),
                                        '& td': { borderBottom: '1px solid #eee' }
                                    }}
                                >
                                    <TableCell className="data-table-cell filename" sx={{ padding: '3px', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                                        <span title={row.fileName}>{
                                            (() => {
                                                const maxLength = 42;
                                                if (row.fileName.length <= maxLength) return row.fileName;

                                                // Check if it's a .csv file
                                                if (row.fileName.toLowerCase().endsWith('.csv')) {
                                                    const nameWithoutExt = row.fileName.slice(0, -4); // Remove .csv
                                                    if (nameWithoutExt.length <= maxLength - 3) return row.fileName; // -3 for "..."
                                                    return nameWithoutExt.substring(0, maxLength - 7) + '...'; // -7 for "...csv"
                                                } else {
                                                    return row.fileName.substring(0, maxLength - 3) + '...';
                                                }
                                            })()
                                        }</span>
                                    </TableCell>
                                    <TableCell className="data-table-cell status" sx={{ padding: '0px', fontSize: '12px', ...getStatusStyle(row.status) }}>{row.status}</TableCell>
                                    <TableCell className="data-table-cell user" sx={{ padding: '0px', fontSize: '12px' }}>{row.createdUser}</TableCell>
                                    <TableCell className="data-table-cell time" sx={{ padding: '0px', fontSize: '12px' }}>{row.createTs}</TableCell>
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
                {data.length > 0 && (
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={2} sx={{ padding: '0px 0px', fontSize: '10px' }}>
                                <div style={{ marginTop: '-6px' }}>
                                    <TablePagination
                                        count={data.length}
                                        page={currentPage}
                                        rowsPerPage={rowsPerPage}
                                        onPageChange={(event, newPage) => onPageChange(newPage)}
                                        rowsPerPageOptions={[]}
                                        showFirstButton={true}
                                        showLastButton={true}
                                        sx={{
                                            '& .MuiTablePagination-toolbar': {
                                                padding: '1px 2px',
                                                minHeight: 'auto',
                                            },
                                            '& .MuiTablePagination-actions': {
                                                '& button': {
                                                    padding: '1px',
                                                },
                                            },
                                            '& .MuiTablePagination-displayedRows': {
                                                fontSize: '10px',
                                            },
                                            '& .MuiTablePagination-selectLabel': {
                                                fontSize: '10px',
                                            },
                                        }}
                                    />
                                </div>
                            </TableCell>
                            <TableCell colSpan={1} sx={{ padding: '0px', textAlign: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center', marginTop: '-6px' }}>
                                    <span style={{ fontSize: '10px', color: '#666' }}>Page:</span>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={jumpToPage}
                                        onChange={(e) => {
                                            const next = e.target.value;
                                            setJumpToPage(next);

                                            // Clear / update validation feedback as the user types
                                            if (next.trim() === '') {
                                                setJumpToPageError(null);
                                                return;
                                            }

                                            const n = parseInt(next, 10);
                                            if (Number.isNaN(n)) {
                                                setJumpToPageError('Please enter a valid page number');
                                            } else if (n < 1 || n > totalPages) {
                                                setJumpToPageError(`Enter 1 - ${totalPages}`);
                                            } else {
                                                setJumpToPageError(null);
                                            }
                                        }}
                                        onKeyPress={handleJumpInputKeyPress}
                                        placeholder={`${currentPage + 1}`}
                                        inputProps={{
                                            min: 1,
                                            max: totalPages,
                                            style: { fontSize: '10px', padding: '0px 0px', width: '20px' }
                                        }}
                                        error={!!jumpToPageError}
                                        helperText={jumpToPageError ?? ''}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                height: '15px',
                                                fontSize: '10px',
                                            },
                                            '& .MuiFormHelperText-root': {
                                                margin: 0,
                                                fontSize: '9px',
                                                lineHeight: 1.1,
                                            },
                                        }}
                                    />
                                    <span style={{ fontSize: '10px', color: '#666' }}>of {totalPages}</span>
                                </div>
                            </TableCell>
                            <TableCell colSpan={1} sx={{ padding: '0px', textAlign: 'right' }}>
                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                    <button onClick={onDownloadAll} style={{ background: 'none', border: 'none', color: '#007bff', textDecoration: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold', padding: '0' }}>
                                        Download All
                                    </button>
                                    <button
                                        onClick={onDownloadSelected}
                                        style={{ background: 'none', border: 'none', color: '#007bff', textDecoration: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold', padding: '0' }}
                                    >
                                        Download Selected
                                    </button>
                                    <button onClick={onClearFilter} style={{ background: 'none', border: 'none', color: '#007bff', textDecoration: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold', padding: '0' }}>
                                        Clear
                                    </button>
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                )}
            </Table>
        </TableContainer>
    );
};
